import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { Dialog, DialogIcon, Input } from "@/components/AdminParts";

export function generateStrongPassword() {
  const lowers = "abcdefghijkmnpqrstuvwxyz", uppers = "ABCDEFGHJKLMNPQRSTUVWXYZ", digits = "23456789", symbols = "!@#$%&*";
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const chars = [...Array.from({ length: 8 }, () => pick(lowers + uppers + digits)), pick(uppers), pick(uppers), pick(digits), pick(symbols)];
  for (let i = chars.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [chars[i], chars[j]] = [chars[j], chars[i]]; }
  return chars.join("");
}

const ROLE_NAME = { machine_owner: "Machine Owner", rental_customer: "Rental Customer" };
const roleName = (r) => ROLE_NAME[r] || "Franchisee";
const portalUrl = () => window.location.origin.replace(/\/+$/, "");

const buildCredentialsMessage = (c) => {
  const greeting = c.full_name ? `Hi ${c.full_name.split(" ")[0]},` : "Hi,";
  const lead = c.mode === "reset" ? `Your ALR ${roleName(c.role)} login has been reset. Here are your new credentials:` : `Welcome to the ALR Portal! Your ${roleName(c.role)} account is ready.`;
  const portal = portalUrl();
  return `${greeting}\n\n${lead}\n\n  Login:    ${portal}\n  Email:    ${c.email}\n  Password: ${c.password}\n\nOpen the ALR app at ${portal} and sign in with the credentials above. Please change your password after your first login.\n\n— Advanced Laser Restoration`;
};

const Title = ({ children }) => <p className="mb-2 text-center text-xl font-extrabold">{children}</p>;
const Body = ({ children }) => <p className="mb-4 text-center text-[13px] leading-[19px]" style={{ color: C.textSecondary }}>{children}</p>;
const Eyebrow = ({ children }) => <p className="mt-3 text-[11px] font-bold tracking-[3px]" style={{ color: C.accent }}>{children}</p>;
const Actions = ({ children }) => <div className="mt-4 flex gap-2">{children}</div>;
const GhostBtn = (p) => <button {...p} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-4 text-xs font-bold tracking-[1.5px] disabled:opacity-50" style={{ borderColor: C.border }} />;
const SolidBtn = ({ bg, busy, children, ...p }) => (
  <button {...p} disabled={busy || p.disabled} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-4 text-xs font-extrabold tracking-[1.2px] text-black disabled:opacity-60" style={{ background: bg }}>
    {busy ? <Loader2 size={16} className="animate-spin" /> : children}
  </button>
);

export const SuspendDialog = ({ target, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [target]);
  const close = () => !busy && onClose();
  const confirm = async () => {
    setBusy(true);
    try {
      const u = await api(`/admin/franchisees/${target.id}/suspend`, { method: "PATCH", body: { is_suspended: true, note: note.trim() || undefined } });
      onDone(u); onClose();
      toast.success(`${u.full_name || u.email} has been suspended. They will be logged out within seconds.`);
    } catch (e) { toast.error(e.message || "Suspend failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onClose={close} testId="suspend-dialog">
      <DialogIcon name="lock-closed" color={C.error} bg="rgba(255,68,68,0.12)" />
      <Title>Suspend Franchisee</Title>
      <Body>{target?.full_name || target?.email} will be logged out within seconds and shown a "Please contact ALR for access to your account" message on every login attempt until reactivated.</Body>
      <p className="mb-1.5 mt-2 text-[10px] font-extrabold tracking-[1.4px]" style={{ color: C.textSecondary }}>INTERNAL NOTE (visible only to admins)</p>
      <textarea data-testid="suspend-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Royalty payment 60 days overdue" rows={3}
        className="w-full rounded-lg border p-3 text-sm text-white placeholder:text-[#A0A0A0] focus:outline-none" style={{ borderColor: C.border, background: C.surface }} />
      <Actions>
        <GhostBtn data-testid="suspend-cancel" onClick={close} disabled={busy}>CANCEL</GhostBtn>
        <SolidBtn data-testid="suspend-confirm" bg={C.error} busy={busy} onClick={confirm}><Ion name="lock-closed" size={14} color="#000" />SUSPEND ACCOUNT</SolidBtn>
      </Actions>
    </Dialog>
  );
};

export const CloseLocationDialog = ({ target, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(""); setReason(""); }, [target]);
  const expected = (target?.full_name || target?.email || "").trim();
  const matches = name.trim().toLowerCase() === expected.toLowerCase() && !!expected;
  const close = () => !busy && onClose();
  const confirm = async () => {
    setBusy(true);
    try {
      const u = await api(`/admin/franchisees/${target.id}/close`, { method: "POST", body: { confirm_name: name, reason: reason.trim() || undefined } });
      onDone(u); onClose();
      toast.success(`${expected} is closed. Their records are kept and you can reopen them any time.`);
    } catch (e) { toast.error(e.message || "Couldn't close this location"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onClose={close} testId="close-location-dialog">
      <DialogIcon name="storefront-outline" color={C.error} bg="rgba(255,68,68,0.12)" />
      <Title>Close Franchise Location</Title>
      <Body>For terminated or closed locations. {expected} and their employees can no longer log in, invoicing is switched off, and they're left out of royalty reminders and broadcasts. Their customers, invoices and royalty history are kept, and you can reopen the account later.</Body>
      <p className="mb-1.5 mt-2 text-[10px] font-extrabold tracking-[1.4px]" style={{ color: C.textSecondary }}>REASON (visible only to admins)</p>
      <textarea data-testid="close-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Franchise agreement terminated" rows={2}
        className="w-full rounded-lg border p-3 text-sm text-white placeholder:text-[#A0A0A0] focus:outline-none" style={{ borderColor: C.border, background: C.surface }} />
      <p className="mb-1.5 mt-3 text-[10px] font-extrabold tracking-[1.4px]" style={{ color: C.textSecondary }}>TYPE <span style={{ color: C.text }}>{expected}</span> TO CONFIRM</p>
      <input data-testid="close-confirm-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={expected}
        className="w-full rounded-lg border p-3 text-sm text-white placeholder:text-[#555] focus:outline-none" style={{ borderColor: matches ? C.error : C.border, background: C.surface }} />
      <Actions>
        <GhostBtn data-testid="close-location-cancel" onClick={close} disabled={busy}>CANCEL</GhostBtn>
        <SolidBtn data-testid="close-location-confirm" bg={C.error} busy={busy} disabled={!matches} onClick={confirm}><Ion name="storefront" size={14} color="#000" />CLOSE LOCATION</SolidBtn>
      </Actions>
    </Dialog>
  );
};

const CountryBtn = ({ active, color, label, ...p }) => (
  <button {...p} className="flex-1 rounded-[10px] border-[1.5px] py-2.5 text-sm font-extrabold" style={{ borderColor: active ? color : C.border, background: active ? `${color}1F` : C.card, color: active ? color : C.text }}>{label}</button>
);

export const EditUserDialog = ({ target, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("US");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) { setName(target.full_name || ""); setEmail(target.email || ""); setCountry(target.country === "CA" ? "CA" : "US"); setNotify(true); } }, [target]);
  const save = async () => {
    const n = name.trim(), em = email.trim().toLowerCase();
    if (n.length < 2) return toast.error("Name must be at least 2 characters.");
    if (!em.includes("@") || em.length < 5) return toast.error("Please enter a valid email address.");
    const nameChanged = n !== (target.full_name || ""), emailChanged = em !== (target.email || "").toLowerCase();
    const countryChanged = country !== (target.country === "CA" ? "CA" : "US");
    if (!nameChanged && !emailChanged && !countryChanged) return onClose();
    const body = {};
    if (nameChanged) body.full_name = n;
    if (emailChanged) { body.email = em; body.notify_user = notify; }
    if (countryChanged) body.country = country;
    setSaving(true);
    try {
      const u = await api(`/admin/users/${target.id}`, { method: "PATCH", body });
      onDone({ id: target.id, full_name: u?.full_name || n, email: u?.email || em, country: u?.country || country });
      toast.success(emailChanged && notify ? "Updated. A heads-up email was sent to both the old and new addresses." : "Updated.");
      onClose();
    } catch (e) { toast.error(e.message || "Couldn't save those changes."); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onClose={() => !saving && onClose()} testId="edit-user-dialog" maxWidth={460}>
      <DialogIcon name="create-outline" color="#A78BFA" bg="rgba(167,139,250,0.14)" />
      <Title>Edit user profile</Title>
      <Body>Fix a typo in the name or login email for <b className="text-white">{target?.full_name || target?.email}</b>. Password and role are not changed.</Body>
      <Eyebrow>FULL NAME</Eyebrow>
      <Input data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" disabled={saving} />
      <Eyebrow>LOGIN EMAIL</Eyebrow>
      <Input data-testid="edit-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" disabled={saving} />
      <Eyebrow>BILLING COUNTRY (QBO CURRENCY)</Eyebrow>
      <div className="mt-1 flex gap-2">
        <CountryBtn data-testid="edit-user-country-us" active={country === "US"} color="#3B82F6" label="USA · USD" onClick={() => !saving && setCountry("US")} />
        <CountryBtn data-testid="edit-user-country-ca" active={country === "CA"} color="#EF4444" label="Canada · CAD" onClick={() => !saving && setCountry("CA")} />
      </div>
      <p className="mt-1.5 text-[11px] leading-[15px]" style={{ color: C.textSecondary }}>Future QBO invoices will be billed in this currency. Existing invoices and customer records are unchanged.</p>
      <button data-testid="edit-user-notify" onClick={() => !saving && setNotify((v) => !v)} className="mt-3 flex w-full items-center gap-2.5 rounded-[10px] border p-2.5 text-left"
        style={{ borderColor: notify ? C.accent : C.border, background: notify ? "rgba(76,204,6,0.06)" : C.cardElevated }}>
        <Ion name={notify ? "checkbox" : "square-outline"} size={16} color={notify ? C.accent : C.textSecondary} />
        <span className="flex-1"><span className="block text-xs font-bold">Notify user of email change</span><span className="mt-0.5 block text-[11px] leading-[15px]" style={{ color: C.textSecondary }}>Sends a heads-up email to both the old and new addresses so they don't get locked out by surprise.</span></span>
      </button>
      <Actions>
        <GhostBtn data-testid="edit-user-cancel" disabled={saving} onClick={onClose}>CANCEL</GhostBtn>
        <SolidBtn data-testid="edit-user-save" bg="#A78BFA" busy={saving} onClick={save}>SAVE CHANGES</SolidBtn>
      </Actions>
    </Dialog>
  );
};

const CredRow = ({ label, value, mono, highlight }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="min-w-[70px] text-[11px] font-bold tracking-[1.2px]" style={{ color: C.textSecondary }}>{label}</span>
    <span className={`flex-1 select-all break-all text-right text-[13px] font-semibold ${mono ? "font-mono" : ""}`} style={highlight ? { color: C.accent, fontWeight: 800 } : undefined} data-testid={`cred-${label.toLowerCase()}`}>{value}</span>
  </div>
);

export const CredentialsDialog = ({ data, onClose }) => {
  if (!data) return null;
  const rn = roleName(data.role);
  const title = data.mode === "reset" ? `${rn} — New Login` : `${rn} Created`;
  const copy = async () => { await navigator.clipboard.writeText(buildCredentialsMessage(data)).catch(() => {}); toast.success("Copied to clipboard"); };
  return (
    <Dialog open onClose={onClose} testId="created-credentials-dialog" borderColor={C.error} maxWidth={460}>
      <DialogIcon name="checkmark-circle" color={C.accent} bg="rgba(76,204,6,0.12)" />
      <Title>{title}</Title>
      <Body>{data.mode === "setup_link"
        ? `A one-time setup link has been emailed to ${data.email}. The link expires in 24 hours and can only be used once. If they don't see it, check spam or use "Send Setup Link" on their row to issue a fresh one.`
        : data.mode === "reset" ? "The password has been reset. Send these new credentials to the user — the password is only shown here once."
          : "Send these credentials to the new user. The password is only shown here once — you'll need to issue a reset if it's lost."}</Body>
      <div className="mt-4 flex flex-col gap-2 rounded-[14px] border p-4" style={{ background: C.cardElevated, borderColor: C.border }}>
        <CredRow label="Name" value={data.full_name || "—"} />
        <CredRow label="Portal" value={portalUrl()} mono />
        <CredRow label="Email" value={data.email} mono />
        {data.mode !== "setup_link" && <CredRow label="Password" value={data.password} mono highlight />}
        <CredRow label="Role" value={rn} />
      </div>
      <div className="mt-4 flex gap-2">
        <button data-testid="copy-credentials" onClick={copy} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-4 text-xs font-extrabold tracking-[1.5px]" style={{ borderColor: C.accent, color: C.accent }}><Ion name="copy-outline" size={14} color={C.accent} />COPY</button>
      </div>
      <button data-testid="close-credentials" onClick={onClose} className="mt-2 w-full py-4 text-xs font-extrabold tracking-[1.5px]" style={{ color: C.textSecondary }}>DONE</button>
    </Dialog>
  );
};
